import { ChangeEvent, FocusEventHandler } from 'react'
import { Grid } from 'theme-ui'
import { Select } from 'components/Form'
import { noop } from 'utils'

type DateInputFieldProps = {
  day?: string | undefined
  month?: string | undefined
  year?: string | undefined
  invalid?: boolean | undefined | string
  maxDate?: {
    year: number
    month: number
    day: number
  }
  disabled?: boolean
  tabIndex?: number
  onChange: (e: ChangeEvent<any>) => void
  onBlur?: FocusEventHandler
}

type range = { start?: number; stop?: number }

function getYearOptions({
  start = 1920,
  stop = new Date().getFullYear(),
}: range): JSX.Element[] {
  const arr = []
  for (let i = stop; i >= start; i--) {
    arr.push(
      <option key={i} value={i}>
        {i}
      </option>
    )
  }
  return arr
}

function getMonthOptions({ start = 1, stop = 12 }: range): JSX.Element[] {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const arr = []
  for (let i = start; i <= stop; i++) {
    const value = i < 10 ? `0${i}` : `${i}`
    arr.push(
      <option key={`month-${i}`} value={value}>
        {months[i - 1]}
      </option>
    )
  }
  return arr
}

function getDayOptions({
  start = 1,
  stop,
  month,
  year,
}: range & { month?: number; year?: number }): JSX.Element[] {
  if (!stop) {
    if (year && month) {
      stop = new Date(year, month, 0).getDate()
    } else if (month) {
      stop = new Date(2004, month, 0).getDate()
    } else {
      stop = 31
    }
  }

  const arr = []
  for (let i = start; i <= stop; i++) {
    const value = i < 10 ? `0${i}` : `${i}`
    arr.push(
      <option key={`day-${i}`} value={value}>
        {value}
      </option>
    )
  }
  return arr
}

export function DateInputField({
  day,
  month,
  year,
  invalid,
  disabled = false,
  maxDate,
  tabIndex = 0,
  onChange,
  onBlur = noop,
}: DateInputFieldProps) {
  const m = isNaN(Number(month)) ? undefined : Number(month)
  const y = isNaN(Number(year)) ? undefined : Number(year)
  const maxYear = maxDate?.year
  const maxMonth = maxDate?.year === y ? maxDate?.month : undefined
  const maxDay =
    maxDate?.year === y && maxDate?.month === m ? maxDate?.day : undefined

  const yearOptions = getYearOptions({ stop: maxYear })
  const monthOptions = getMonthOptions({ stop: maxMonth })
  const dayOptions = getDayOptions({ stop: maxDay, month: m, year: y })

  return (
    <Grid columns="105px 105px 105px" gap={4} data-cy="Date-input">
      <Select
        name="__month"
        data-testid="select__month"
        value={month}
        aria-invalid={invalid ? true : false}
        selectSx={{ cursor: 'pointer' }}
        disabled={disabled}
        tabIndex={tabIndex}
        onBlur={onBlur}
        onChange={onChange}>
        <option value="">Month</option>
        {monthOptions}
      </Select>
      <Select
        name="__day"
        data-testid="select__day"
        value={day}
        aria-invalid={invalid ? true : false}
        selectSx={{ cursor: 'pointer' }}
        disabled={disabled}
        tabIndex={tabIndex + 0.1}
        onBlur={onBlur}
        onChange={onChange}>
        <option value="">Day</option>
        {dayOptions}
      </Select>
      <Select
        name="__year"
        data-testid="select__year"
        value={year}
        selectSx={{ cursor: 'pointer' }}
        aria-invalid={invalid ? true : false}
        disabled={disabled}
        tabIndex={tabIndex + 0.2}
        onBlur={onBlur}
        onChange={onChange}>
        <option value="">Year</option>
        {yearOptions}
      </Select>
    </Grid>
  )
}
